import { CDN_BASE } from "../config";

/**
 * The single point every asset URL passes through (EB-513).
 *
 * The catalog stores two different things in `picture`/`images` today, and will store a third
 * after EB-516 migrates them, so this accepts all of them. That tolerance is the whole point: it
 * lets the consumers be changed first and the stored data migrated later, instead of both in one
 * deploy.
 *
 * Measured against prod on 2026-07-27, of 296 active products:
 *
 * - **276** hold an absolute S3 URL (`https://entorno-biai.s3.us-east-2.amazonaws.com/...`)
 * - **5** hold a *third-party* URL (`media.falabella.com.co`) — not ours, and must pass untouched
 * - **15** hold nothing at all
 * - 0 hold a bare key yet; that is what EB-516 produces
 *
 * The third-party case is the one worth knowing about: the ticket only anticipated our own S3 URLs
 * and keys, and rewriting a Falabella URL onto our CDN would 404 five live products.
 */

/**
 * Where assets lived before the CDN.
 *
 * No longer a fallback — `CDN_BASE` defaults to the CDN since EB-517, because a fallback to the
 * bucket resolves to a 403 once public reads are gone. This is kept because it is still the value
 * stored in rows EB-516 did not convert (`banners`, `education-items`), which `resolveAssetUrl`
 * has to recognise on input, and because it is what you set `REACT_APP_CDN_BASE` to if you ever
 * need a build to bypass the CDN.
 */
export const LEGACY_S3_BASE = "https://entorno-biai.s3.us-east-2.amazonaws.com";

const BUCKET = "entorno-biai";

/** Virtual-hosted (`bucket.s3[.region].amazonaws.com`) and path-style (`s3[.region]…/bucket`). */
const S3_HOST_PATTERNS = [
  new RegExp(`^https?://${BUCKET}\\.s3(?:[.-][a-z0-9-]+)?\\.amazonaws\\.com/(.+)$`, "i"),
  new RegExp(`^https?://s3(?:[.-][a-z0-9-]+)?\\.amazonaws\\.com/${BUCKET}/(.+)$`, "i"),
];

const isAbsolute = (value: string) => /^https?:\/\//i.test(value);

/** The key inside our bucket, or `null` when the URL points somewhere that isn't ours. */
const keyFromOurS3Url = (url: string): string | null => {
  for (const pattern of S3_HOST_PATTERNS) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

/**
 * Resolves a stored asset value to something an `<img src>` can use.
 *
 * Empty input returns an empty string — today's behaviour, preserved deliberately. The ticket
 * describes returning "the missing-image placeholder already in use", but there isn't one: no
 * placeholder asset exists in `public/`, and 15 active products already render with an empty
 * `src`. Inventing one here would be a visual change smuggled into a plumbing ticket, so the
 * decision is left to whoever owns that design.
 */
export const resolveAssetUrl = (keyOrUrl?: string | null): string => {
  const value = keyOrUrl?.trim();
  if (!value) return "";

  if (isAbsolute(value)) {
    const key = keyFromOurS3Url(value);

    // Not our bucket — a supplier's CDN, say. Pass it through untouched; rewriting it onto ours
    // would point at an object that does not exist.
    if (key === null) return value;

    // Ours, so it is rewritten onto the CDN unconditionally. This is the branch that still matters
    // after EB-516: `banners` and `education-items` were never converted to keys, so they reach
    // here as absolute S3 URLs and would 403 if returned untouched once the bucket is closed.
    return `${CDN_BASE}/${key}`;
  }

  // A bare key. Leading slashes are stripped so `/productos_online/x.jpg` and
  // `productos_online/x.jpg` cannot produce a double slash and a cache miss against the same file.
  const key = value.replace(/^\/+/, "");
  return `${CDN_BASE}/${key}`;
};

/**
 * Where a product's thumbnail lives (EB-518) — the mirror of `toThumbKey` in
 * `lambda/thumbnails/utils/thumbKey.mjs`, with the same examples pinned in both test suites.
 *
 * Derived rather than stored, so no column and no data migration: the card asks for the thumbnail
 * and falls back to the original if it 404s. That fallback is what lets this ship before a single
 * thumbnail exists, and what makes a failed generation invisible instead of a broken card.
 *
 * Returns `""` when there can be no thumbnail — a third-party URL, an unsupported file type, an
 * empty value — which the caller reads as "just use the original".
 */
const THUMB_CONVERTIBLE = /\.(png|jpe?g|webp)$/i;

export const resolveThumbUrl = (keyOrUrl?: string | null): string => {
  const value = keyOrUrl?.trim();
  if (!value) return "";

  // Absolute URLs are somebody else's bytes; 5 active products point at media.falabella.com.co.
  if (isAbsolute(value)) return "";

  const key = value.replace(/^\/+/, "");
  if (!THUMB_CONVERTIBLE.test(key) || key.startsWith("thumbs/")) return "";

  return resolveAssetUrl(`thumbs/${key.replace(THUMB_CONVERTIBLE, ".webp")}`);
};
